import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Globe, Smartphone, Settings, ArrowRight, Check } from 'lucide-react';

const ProductsServices = () => {
  const services = [
    {
      icon: <Globe className="h-12 w-12" />,
      title: "Web Development",
      shortDesc: "Custom websites and web applications built with modern technologies.",
      description: "We create responsive, scalable web solutions that deliver exceptional user experiences and drive business growth. From simple websites to complex web applications, our team leverages the latest technologies to build solutions that work seamlessly across all devices.",
      features: [
        "Responsive Design & Development",
        "Custom Web Applications",
        "E-commerce Solutions",
        "Content Management Systems",
        "Performance Optimization",
        "SEO Integration"
      ],
      technologies: ["React", "Node.js", "Python", "PostgreSQL", "AWS", "Docker"]
    },
    {
      icon: <Smartphone className="h-12 w-12" />,
      title: "App Development",
      shortDesc: "Native and cross-platform mobile applications for iOS and Android.",
      description: "Our mobile development team creates engaging, performant applications that connect with your users wherever they are. We specialize in both native and cross-platform development, ensuring your app delivers a premium experience on every device.",
      features: [
        "Native iOS & Android Development",
        "Cross-platform Solutions",
        "UI/UX Design",
        "App Store Optimization",
        "Performance Monitoring",
        "Maintenance & Updates"
      ],
      technologies: ["React Native", "Flutter", "Swift", "Kotlin", "Firebase", "AWS Mobile"]
    },
    {
      icon: <Settings className="h-12 w-12" />,
      title: "IT Consulting",
      shortDesc: "Strategic technology consulting to optimize your digital infrastructure.",
      description: "Our experienced consultants help businesses navigate the complex technology landscape. From digital transformation strategies to infrastructure optimization, we provide the expertise you need to make informed technology decisions and drive growth.",
      features: [
        "Digital Transformation Strategy",
        "Technology Assessment",
        "Infrastructure Planning",
        "Security Consulting",
        "Process Optimization",
        "Training & Support"
      ],
      technologies: ["Cloud Platforms", "DevOps", "Cybersecurity", "Analytics", "AI/ML", "Automation"]
    }
  ];

  const products = [
    {
      name: "GoFella",
      tagline: "Your Digital Companion",
      description: "GoFella is our flagship product designed to simplify daily tasks and enhance productivity. This innovative platform combines intuitive design with powerful functionality to help users manage their work and personal life more effectively.",
      features: [
        "Task Management & Organization",
        "Smart Notifications",
        "Team Collaboration Tools",
        "Analytics & Insights",
        "Mobile & Desktop Apps",
        "API Integration"
      ],
      status: "Coming Soon",
      image: "https://images.unsplash.com/photo-1594235048794-fae8583a5af5",
      link: "#"
    }
  ];

  const upcomingProducts = [
    {
      name: "Vincotek Analytics",
      description: "Advanced business intelligence platform for data-driven decision making.",
      status: "Coming Soon"
    },
    {
      name: "SecureConnect",
      description: "Enterprise-grade communication and collaboration platform.",
      status: "In Development"
    },
    {
      name: "SmartFlow",
      description: "Workflow automation tool for streamlined business processes.",
      status: "Planning Phase"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-white via-blue-50/30 to-yellow-50/20 py-20 lg:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-vincotek-dark leading-tight mb-6">
              Products &{' '}
              <span className="bg-gradient-to-r from-vincotek-yellow to-vincotek-blue bg-clip-text text-transparent">
                Services
              </span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-12">
              Discover our comprehensive range of technology solutions designed to empower your business, 
              streamline operations, and drive growth in the digital age.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#services">
                <Button className="bg-vincotek-blue hover:bg-blue-600 text-white px-8 py-3 rounded-full transition-all duration-300 hover:shadow-lg">
                  Explore Services
                </Button>
              </a>
              <a href="#products">
                <Button variant="outline" className="border-vincotek-yellow text-vincotek-dark hover:bg-vincotek-yellow px-8 py-3 rounded-full transition-all duration-300">
                  View Products
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-6">
              Our Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We offer comprehensive technology services to help businesses thrive in the digital landscape.
            </p>
          </div>
          
          <div className="space-y-20">
            {services.map((service, index) => (
              <div key={index} className={`${index % 2 === 1 ? 'lg:flex-row-reverse' : ''} flex flex-col lg:flex-row items-center gap-12 lg:gap-16`}>
                {/* Content */}
                <div className="lg:w-1/2 space-y-6">
                  <div className="flex items-center space-x-4">
                    <div className="text-vincotek-blue">
                      {service.icon}
                    </div>
                    <h3 className="text-3xl font-bold text-vincotek-dark">
                      {service.title}
                    </h3>
                  </div>
                  
                  <p className="text-lg text-gray-600 leading-relaxed">
                    {service.description}
                  </p>
                  
                  {/* Features */}
                  <div className="space-y-3">
                    <h4 className="text-lg font-semibold text-vincotek-dark">Key Features:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {service.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center space-x-2">
                          <Check className="h-4 w-4 text-vincotek-blue flex-shrink-0" />
                          <span className="text-sm text-gray-600">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Technologies */}
                  <div>
                    <h4 className="text-lg font-semibold text-vincotek-dark mb-3">Technologies:</h4>
                    <div className="flex flex-wrap gap-2">
                      {service.technologies.map((tech, techIndex) => (
                        <span
                          key={techIndex}
                          className="bg-gradient-to-r from-vincotek-yellow/20 to-vincotek-blue/20 text-vincotek-dark px-3 py-1 rounded-full text-sm font-medium"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <Link to={`/services/${service.title.toLowerCase().replace(/\s+/g, '-')}`}>
                    <Button className="mt-4 bg-vincotek-yellow hover:bg-yellow-400 text-vincotek-dark px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg group">
                      Learn More
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </div>
                
                {/* Visual */}
                <div className="lg:w-1/2">
                  <Card className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-white border-0">
                    <CardContent className="p-8">
                      <div className="w-full h-64 bg-gradient-to-br from-vincotek-yellow/20 to-vincotek-blue/20 rounded-xl flex items-center justify-center mb-6">
                        <div className="text-vincotek-blue group-hover:scale-110 transition-transform duration-300">
                          {React.cloneElement(service.icon, { className: "h-20 w-20" })}
                        </div>
                      </div>
                      <h4 className="text-xl font-semibold text-vincotek-dark mb-3">
                        {service.title}
                      </h4>
                      <p className="text-gray-600 leading-relaxed">
                        {service.shortDesc}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-6">
              Our Products
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Innovative solutions designed to solve real-world problems and enhance productivity.
            </p>
          </div>
          
          {/* Featured Product - GoFella */}
          <div className="mb-20">
            {products.map((product, index) => (
              <div key={index} className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div className="space-y-6">
                  <div>
                    <div className="flex items-center space-x-3 mb-4">
                      <h3 className="text-3xl font-bold text-vincotek-dark">
                        {product.name}
                      </h3>
                      <span className="bg-vincotek-blue text-white px-3 py-1 rounded-full text-sm font-medium">
                        {product.status}
                      </span>
                    </div>
                    <p className="text-lg font-medium text-vincotek-blue mb-4">
                      {product.tagline}
                    </p>
                    <p className="text-gray-600 leading-relaxed">
                      {product.description}
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-semibold text-vincotek-dark mb-3">Key Features:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {product.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center space-x-2">
                          <Check className="h-4 w-4 text-vincotek-blue flex-shrink-0" />
                          <span className="text-sm text-gray-600">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="flex space-x-4">
                    <Button className="bg-vincotek-blue hover:bg-blue-600 text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg">
                      Try GoFella
                    </Button>
                    <Link to="/products/gofella">
                      <Button variant="outline" className="border-vincotek-blue text-vincotek-blue hover:bg-vincotek-blue hover:text-white px-8 py-3 rounded-full transition-all duration-300">
                        Learn More
                      </Button>
                    </Link>
                  </div>
                </div>
                
                <div className="relative">
                  <img 
                    src={product.image} 
                    alt={product.name} 
                    className="w-full h-80 object-cover rounded-2xl shadow-2xl"
                  />
                  <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-6 shadow-xl border border-gray-100">
                    <div className="text-vincotek-dark text-lg font-bold">{product.name}</div>
                    <div className="text-gray-600 text-sm">{product.tagline}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Upcoming Products */}
          <div>
            <div className="text-center mb-12">
              <h3 className="text-2xl font-bold text-vincotek-dark mb-4">
                Coming Soon
              </h3>
              <p className="text-lg text-gray-600">
                Exciting new products in development
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {upcomingProducts.map((product, index) => (
                <Card key={index} className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-white border-0">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-vincotek-yellow to-vincotek-blue rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                      <span className="text-white font-bold text-lg">{index + 2}</span>
                    </div>
                    <h4 className="text-xl font-semibold text-vincotek-dark mb-3">
                      {product.name}
                    </h4>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      {product.description}
                    </p>
                    <span className="inline-block bg-gradient-to-r from-vincotek-yellow/20 to-vincotek-blue/20 text-vincotek-dark px-4 py-2 rounded-full text-sm font-medium">
                      {product.status}
                    </span>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-vincotek-dark text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Let's discuss how our products and services can help transform your business 
            and achieve your technology goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-vincotek-yellow hover:bg-yellow-400 text-vincotek-dark px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg">
                Start a Project
              </Button>
            </Link>
            <Link to="/about">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-vincotek-dark px-8 py-3 rounded-full transition-all duration-300">
                Learn About Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductsServices;